import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

/**
 * Props interface for the CategoryPageRecentlyViewed component
 * @interface CategoryPageRecentlyViewedProps
 * @property {DisplayMode} displayMode - Current display mode (mobile/desktop)
 * @property {TileableModel[]} items - Array of recently viewed items to display
 */
interface CategoryPageRecentlyViewedProps {
    displayMode: DisplayMode;
    items: TileableModel[];
}

/**
 * Component that renders a horizontal scrollable carousel of recently viewed items
 * Shows items in a grid with different sizes for desktop/mobile
 * @param {CategoryPageRecentlyViewedProps} props - Component props
 * @returns {JSX.Element | null} The rendered carousel section or null if no items
 */
export const CategoryPageRecentlyViewed = (
    props: CategoryPageRecentlyViewedProps
) => {
    const { displayMode, items } = props;
    const isDesktop = displayMode === DisplayMode.Desktop;
    const { handleFollow, isInDesignBoard } = useHandleFollow();

    const recentlyViewedTiles: TileViewModel<TileableModel>[] = items.map(
        (item) => ({
            aspectRatio: isProductTileViewModel(item) ? 'square' : 'default',
            model: item,
            section: 'CategoryPageRecentlyViewed',
            isFollowed: isInDesignBoard(item.id),
            onFollow: (pinId, pinType) => handleFollow({ id: pinId }, pinType),
        })
    );

    if (recentlyViewedTiles.length === 0) {
        return null;
    }

    return (
        <CarouselSection
            title="Recently viewed"
            size={isDesktop ? 'recentlyViewed' : 'small'}
        >
            {recentlyViewedTiles.map((tile) => (
                <Tile
                    key={`recentlyViewedTiles-${tile.model.id}`}
                    tile={tile}
                />
            ))}
        </CarouselSection>
    );
};
